import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, money } from "@/lib/api";

const NUMS = [["initial_fee", "Initial fee (Item 5)"], ["royalty_pct", "Royalty % (Item 6)"], ["marketing_pct", "Marketing fund % (Item 6)"],
  ["investment_low", "Investment low (Item 7)"], ["investment_high", "Investment high (Item 7)"],
  ["item19.avg_revenue", "Avg unit revenue (Item 19)"], ["item19.median_revenue", "Median unit revenue (Item 19)"], ["item20.total_units", "Units in system (Item 20)"]];
const get = (f, k) => k.split(".").reduce((o, p) => o?.[p], f);
const toNum = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const field = "bg-slate-950/60 border-slate-700 h-9 text-sm";

export const FddReview = ({ doc, onSaved, onCancel, testId = "fdd" }) => {
  const f0 = doc.figures || {};
  const [vals, setVals] = useState(() => Object.fromEntries(NUMS.map(([k]) => [k, get(f0, k) ?? ""])));
  const [s19, setS19] = useState(f0.item19?.summary || "");
  const [s20, setS20] = useState(f0.item20?.summary || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const n = (k) => toNum(vals[k]);
    const body = { ...f0, initial_fee: n("initial_fee"), royalty_pct: n("royalty_pct"), marketing_pct: n("marketing_pct"), investment_low: n("investment_low"), investment_high: n("investment_high"),
      item19: { ...(f0.item19 || {}), summary: s19, avg_revenue: n("item19.avg_revenue"), median_revenue: n("item19.median_revenue") },
      item20: { ...(f0.item20 || {}), summary: s20, total_units: vals["item20.total_units"] === "" ? null : Math.round(Number(vals["item20.total_units"])) } };
    try { const r = await api.put(`/fdd/${doc.id}`, body); onSaved(r.data); toast.success("FDD figures confirmed"); }
    catch (e) { toast.error(errMsg(e, "Could not save the figures")); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid={`${testId}-review`}>
      <p className="text-sm text-slate-300">Check the figures the AI read from <span className="text-emerald-300">{doc.filename}</span>{f0.fdd_year ? ` (${f0.fdd_year})` : ""}. Fix anything that looks wrong, then confirm.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {NUMS.map(([k, label]) => (
          <label key={k} className="space-y-1 text-[11px] uppercase tracking-wider text-slate-400">{label}
            <Input data-testid={`${testId}-review-${k.replace(/[._]/g, "-")}`} type="number" step="any" value={vals[k]} onChange={(e) => setVals((v) => ({ ...v, [k]: e.target.value }))} className={`${field} num normal-case`} />
          </label>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="space-y-1 text-[11px] uppercase tracking-wider text-slate-400">Item 19 summary<Textarea data-testid={`${testId}-review-item19-summary`} rows={3} value={s19} onChange={(e) => setS19(e.target.value)} className="bg-slate-950/60 border-slate-700 text-sm normal-case" /></label>
        <label className="space-y-1 text-[11px] uppercase tracking-wider text-slate-400">Item 20 summary<Textarea data-testid={`${testId}-review-item20-summary`} rows={3} value={s20} onChange={(e) => setS20(e.target.value)} className="bg-slate-950/60 border-slate-700 text-sm normal-case" /></label>
      </div>
      {!!f0.item7?.length && (
        <details className="text-sm"><summary className="cursor-pointer text-xs text-slate-400">Item 7 line items ({f0.item7.length})</summary>
          <table className="mt-2 w-full text-xs"><tbody>{f0.item7.map((r, i) => <tr key={i} className="border-t border-slate-800"><td className="py-1 pr-2 text-slate-300">{r.item}</td><td className="py-1 num text-right text-slate-400">{money(r.low)} – {money(r.high)}</td></tr>)}</tbody></table>
        </details>
      )}
      <div className="flex gap-2">
        <Button type="button" size="sm" data-testid={`${testId}-review-confirm`} disabled={saving} onClick={save} className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}Use these figures</Button>
        {onCancel && <Button type="button" size="sm" variant="ghost" data-testid={`${testId}-review-cancel`} onClick={onCancel}>Cancel</Button>}
      </div>
    </div>
  );
};
